<template>
  <v-card class="pa-4 linea-paros-card" elevation="1">
    <div v-if="chartData.length" class="mb-4">
      <div class="px-4 pt-1 pb-2">
        <div class="subtitle-2 font-weight-medium">
          {{ tituloPareto }}
        </div>
      </div>

      <div class="px-4 pb-3 d-flex justify-center">
        <div class="rounded-pill grey lighten-4 pa-1 d-inline-flex elevation-1">
          <v-btn small fab class="mx-1" :color="modoActual === 'minutos' ? 'primary' : null"
            :aria-pressed="modoActual === 'minutos'" @click="setModo('minutos')">
            <v-icon small :color="modoActual === 'minutos' ? 'white' : 'primary'">
              mdi-timer-outline
            </v-icon>
          </v-btn>

          <v-btn small fab class="mx-1" :color="modoActual === 'porcentaje' ? 'primary' : null"
            :aria-pressed="modoActual === 'porcentaje'" @click="setModo('porcentaje')">
            <v-icon small :color="modoActual === 'porcentaje' ? 'white' : 'primary'">
              mdi-percent-outline
            </v-icon>
          </v-btn>

          <v-btn small fab class="mx-1" :color="modoActual === 'tipo' ? 'primary' : null"
            :aria-pressed="modoActual === 'tipo'" @click="setModo('tipo')">
            <v-icon small :color="modoActual === 'tipo' ? 'white' : 'primary'">
              mdi-shape-outline
            </v-icon>
          </v-btn>
        </div>
      </div>

      <div class="px-2">
        <apexchart type="bar" :options="chartOpts" :series="chartSeries" height="300" />
      </div>

      <v-divider class="my-4" />
    </div>

    <div v-if="timelineItems.length" class="hora-timeline">
      <div class="subtitle-2 font-weight-medium mb-2 px-1">
        Historial de paros
      </div>

      <div class="hora-timeline-list">
        <div v-for="item in timelineItems" :key="item.key" class="hora-timeline-row">
          <div class="hora-timeline-dot" :style="{ backgroundColor: item.color }" />

          <div class="hora-timeline-content">
            <div class="subtitle-2 grey--text text--darken-1 mb-1">
              {{ item.rango }}
            </div>

            <div class="h6 font-weight-medium">
              {{ item.descripcion }}
            </div>

            <div class="h6 grey--text">
              Duracion: {{ item.duracionTxt }}
              <span v-if="item.idsTxt">
                <!-- | IDs: {{ item.idsTxt }} -->
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center caption my-6">
      Sin datos de paros.
    </div>
  </v-card>
</template>

<script>
const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'DialgoParosHpH',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    detallesParos: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      colors: {
        paro: '#EF5350',
      },
      modoActual: 'minutos',
    };
  },
  computed: {
    sourceValue() {
      return this.value && typeof this.value === 'object' ? this.value : {};
    },
    parosRaw() {
      if (Array.isArray(this.detallesParos)) return this.detallesParos;

      const arr = this.pick(this.sourceValue, ['DetallesParos', 'detallesParos']);
      return Array.isArray(arr) ? arr : [];
    },
    timelineItems() {
      const out = [];

      this.parosRaw.forEach((paro, idx) => {
        const start = this.parseDate(this.pick(paro, ['Inicio', 'inicio']));
        const endBase = this.parseDate(this.pick(paro, ['Fin', 'fin']));
        if (!start || !endBase) return;

        const end = endBase <= start
          ? new Date(endBase.getTime() + DAY_MS)
          : endBase;

        const seconds = (end - start) / 1000;
        if (!(seconds > 0)) return;

        const causa = this.pick(paro, ['Causa', 'causa']) || '';
        const subCausa = this.pick(paro, ['SubCausa', 'subCausa']) || '';
        const idsRaw = this.pick(paro, ['IdsParos', 'idsParos']);
        const idsParos = Array.isArray(idsRaw) ? idsRaw : [];

        out.push({
          key: `${start.toISOString()}-${idx}`,
          start,
          end,
          seconds,
          descripcion: this.labelParo(causa, subCausa),
          color: this.colors.paro,
          idsTxt: this.idsToText(idsParos),
        });
      });

      out.sort((a, b) => a.start - b.start || a.end - b.end);

      return out.map(item => ({
        ...item,
        rango: `${this.dateToHHmm(item.start)} - ${this.dateToHHmm(item.end)}`,
        duracionTxt: this.formatDuration(item.seconds),
      }));
    },
    tituloPareto() {
      return this.modoActual === 'tipo' ? 'PAROS POR TIPO' : 'PARETO DE PAROS';
    },
    tipoData() {
      const orden = ['programado', 'no programado', 'sin clasificar'];
      const grupos = new Map(orden.map(key => [key, {
        key,
        label: key === 'programado'
          ? 'Paro programado'
          : key === 'no programado'
            ? 'Paro no programado'
            : 'Sin clasificar',
        seconds: 0,
        eventos: 0,
        color: key === 'programado' ? '#42A5F5' : key === 'no programado' ? '#EF5350' : '#78909C',
      }]));
      let totalSegundos = 0;

      this.parosRaw.forEach((paro) => {
        const seconds = this.paroSeconds(paro);
        if (!(seconds > 0)) return;

        const key = this.tipoParoKey(paro);
        const item = grupos.get(key) || grupos.get('sin clasificar');
        item.seconds += seconds;
        item.eventos += 1;
        totalSegundos += seconds;
      });

      return orden
        .map(key => grupos.get(key))
        .filter(item => item && item.seconds > 0)
        .map(item => ({
          ...item,
          minutos: Math.round(item.seconds / 60),
          porcentaje: totalSegundos > 0 ? (item.seconds * 100) / totalSegundos : 0,
        }));
    },
    paretoData() {
      const grupos = new Map();
      let totalSegundos = 0;

      this.timelineItems.forEach(item => {
        const label = item.descripcion || 'SIN CAUSA';
        const key = this.normaliza(label);

        let g = grupos.get(key);
        if (!g) {
          g = {
            key,
            label,
            seconds: 0,
            eventos: 0,
          };
          grupos.set(key, g);
        }

        g.seconds += Number(item.seconds || 0);
        g.eventos += 1;
        totalSegundos += Number(item.seconds || 0);
      });

      const arr = Array.from(grupos.values()).filter(g => g.seconds > 0);
      arr.forEach(g => {
        g.minutos = Math.round(g.seconds / 60);
        g.porcentaje = totalSegundos > 0 ? (g.seconds * 100) / totalSegundos : 0;
        g.color = this.colors.paro;
      });

      return arr.sort((a, b) => b.seconds - a.seconds);
    },
    chartData() {
      return this.modoActual === 'tipo' ? this.tipoData : this.paretoData;
    },
    chartCategorias() {
      return this.chartData.map(g => {
        const txt = String(g.label || '');
        if (this.modoActual === 'tipo') return txt;
        return txt.length > 20 ? `${txt.slice(0, 20)}...` : txt;
      });
    },
    chartSeries() {
      if (!this.chartData.length) return [];

      if (this.modoActual === 'porcentaje') {
        return [{
          name: '% del tiempo de paro',
          data: this.chartData.map(g => Number(g.porcentaje.toFixed(2))),
        }];
      }

      if (this.modoActual === 'tipo') {
        return [{
          name: 'Tiempo de paro',
          data: this.chartData.map(g => g.minutos),
        }];
      }

      return [{
        name: 'Minutos de paro',
        data: this.chartData.map(g => g.minutos),
      }];
    },
    chartColors() {
      return this.chartData.map(g => g.color || this.colors.paro);
    },
    chartOpts() {
      if (!this.chartSeries.length) return {};

      const serie = this.chartSeries[0];
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            distributed: true,
            horizontal: true,
            barHeight: '60%',
            borderRadius: 6,
            dataLabels: { position: 'right' },
          },
        },
        colors: this.chartColors,
        xaxis: {
          categories: this.chartCategorias,
          title: { text: serie.name },
        },
        yaxis: {
          labels: { style: { fontSize: '12px' } },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '12px', fontWeight: 700, colors: ['#000'] },
          background: {
            enabled: true,
            foreColor: '#fff',
            borderRadius: 4,
            padding: 4,
            opacity: 1,
            borderWidth: 0,
            borderColor: '#fff',
            dropShadow: { enabled: false },
          },
          formatter: (val, opts) => {
            if (this.modoActual === 'porcentaje') {
              return `${Number(val).toFixed(2)}%`;
            }

            if (this.modoActual === 'tipo') {
              const d = this.chartData[opts?.dataPointIndex] || {};
              const pct = (Number(d.porcentaje) || 0).toFixed(2);
              return `${val} min | ${pct}%`;
            }

            return `${val} min`;
          },
        },
        tooltip: {
          y: {
            formatter: (val, opts) => {
              const d = this.chartData[opts.dataPointIndex] || {};
              const minutos = Math.round((Number(d.seconds) || 0) / 60);
              const pct = (Number(d.porcentaje) || 0).toFixed(2);
              const ev = Number(d.eventos || 0);
              return `${d.label || 'Paro'} | ${ev} evento(s) | ${minutos} min | ${pct}%`;
            },
          },
        },
        grid: { padding: { left: 6, right: 6 } },
      };
    },
  },
  methods: {
    setModo(modo) {
      this.modoActual = modo;
    },
    paroSeconds(paro) {
      const directos = Number(this.pick(paro, ['Segundos', 'segundos']));
      if (Number.isFinite(directos) && directos > 0) return directos;

      const start = this.parseDate(this.pick(paro, ['Inicio', 'inicio']));
      const endBase = this.parseDate(this.pick(paro, ['Fin', 'fin']));
      if (!start || !endBase) return 0;

      const end = endBase <= start ? new Date(endBase.getTime() + DAY_MS) : endBase;
      const seconds = Math.round((end - start) / 1000);
      return seconds > 0 ? seconds : 0;
    },
    tipoParoKey(paro) {
      if (this.pick(paro, ['paroProgramado', 'ParoProgramado']) === true) return 'programado';
      if (this.pick(paro, ['paroNoProgramado', 'ParoNoProgramado']) === true) return 'no programado';
      return 'sin clasificar';
    },
    pick(obj, keys) {
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (obj && obj[k] != null) return obj[k];
      }
      return undefined;
    },
    parseDate(value) {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    },
    dateToHHmm(dt) {
      const d = dt instanceof Date ? dt : new Date(dt);
      if (!d || Number.isNaN(d.getTime())) return '--:--';
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    labelParo(causa, subCausa) {
      const c = String(causa || '').trim();
      const s = String(subCausa || '').trim();
      if (c && s) return `${c} - ${s}`;
      if (c) return c;
      if (s) return s;
      return 'Paro';
    },
    idsToText(ids) {
      if (!Array.isArray(ids) || !ids.length) return '';
      return ids
        .map(v => Number(v))
        .filter(v => Number.isFinite(v))
        .join(', ');
    },
    normaliza(txt) {
      return String(txt || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    },
    formatDuration(segundos) {
      const total = Math.max(0, Math.floor(Number(segundos) || 0));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;

      if (h > 0) return `${h} h ${m} min ${s} s`;
      if (m > 0) return `${m} min ${s} s`;
      return `${s} s`;
    },
  },
};
</script>

<style scoped>
.linea-paros-card {
  border-radius: 8px;
}

.hora-timeline {
  margin-top: 8px;
}

.hora-timeline-list {
  position: relative;
  padding-left: 32px;
}

.hora-timeline-list::before {
  content: "";
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e0e0e0;
}

.hora-timeline-row {
  display: flex;
  position: relative;
  margin-bottom: 18px;
}

.hora-timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.12);
  position: absolute;
  left: 9px;
  top: 4px;
}

.hora-timeline-content {
  margin-left: 24px;
  flex: 1;
}
</style>
